import type { Account } from '@api/hooks/useAccounts';
import { useAccounts } from '@api/hooks/useAccounts';
import Alert from '@components/alert';
import { NoFilteredResults } from '@components/EmptyState';
import { PageContainer } from '@components/PageContainer';
import type { ColumnProps } from '@gurupay/gp-ui-library';
import {
	AccountsIcon,
	Button,
	DropdownPositions,
	FileAddIcon,
	Pagination,
	Table,
} from '@gurupay/gp-ui-library';
import { useClearFilter } from '@hooks/useClearFilter';
import { usePagination } from '@hooks/usePagination';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import type { AccountFilters } from './components';
import {
	AccountCellDesktop,
	AccountCellMobile,
	AccountFilter,
	ALL_ACCOUNT_FILTER_STATUSES,
	AvailableBalanceCell,
} from './components';
import { EMPTY_CELL_PLACEHOLDER, getAmountLabel } from './utils';

export const Accounts = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		currentPage,
		setCurrentPage,
		pageSize,
		pageOptions,
		handlePageSizeChange,
		pageText,
		totalCountText,
	} = usePagination();
	const [filters, setFilters] = useState<AccountFilters>({
		status: ALL_ACCOUNT_FILTER_STATUSES,
	});

	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
	const mobileColumns: ColumnProps[] = [
		{
			label: t('dashboard.accountInformationCard.account'),
			key: 'account',
			align: 'left',
		},
	];
	const columns: ColumnProps[] = [
		{
			label: t('dashboard.accountInformationCard.account'),
			key: 'account',
			align: 'left',
		},
		{
			label: t('dashboard.accountInformationCard.currency'),
			key: 'currency',
			align: 'left',
		},
		{
			label: t('dashboard.accountInformationCard.balance'),
			key: 'balance',
		},
		{
			label: t('dashboard.accountInformationCard.reserved'),
			key: 'reservedAmount',
		},
		{
			label: t('dashboard.accountInformationCard.availableBalance'),
			key: 'availableBalance',
		},
	];
	const renders = {
		availableBalance: AvailableBalanceCell,
		reservedAmount: ({ reservedAmount, status }: Account) =>
			getAmountLabel(reservedAmount, status),
		balance: ({ balance, status }: Account) => getAmountLabel(balance, status),
		account: isMobile ? AccountCellMobile : AccountCellDesktop,
	};

	const {
		data: accounts,
		isLoading,
		isError,
	} = useAccounts({
		currentPage,
		limit: pageSize,
		...filters,
	});

	const filterForm = useForm<AccountFilters>({
		defaultValues: {
			status: ALL_ACCOUNT_FILTER_STATUSES,
		},
	});
	const handleFilterApply = (filterOptions: AccountFilters) => {
		setFilters(filterOptions);
		setCurrentPage(1);
	};
	const { hasFilters, handleClear } = useClearFilter<AccountFilters>(
		filterForm,
		handleFilterApply,
	);

	const handleRowClick = (acc: Account) => {
		navigate(`/accounts/${acc.uuid}`);
	};

	const checkAccountStatus = ({ status }: Account) => status === 'CLOSED';

	return (
		<PageContainer icon={<AccountsIcon />} title={t('accounts.title')}>
			{isError ? (
				<Alert type="error">{t('common.failedFetching')}</Alert>
			) : (
				<>
					<div className="flex gap-space-8">
						<Button
							href="/statements"
							className="flex-row-reverse w-max"
							icon={<FileAddIcon className="w-space-16" />}
							variant="primary-black"
						>
							{t('statements.statements')}
						</Button>
						{(!!accounts?.items.length || hasFilters || isLoading) && (
							<FormProvider {...filterForm}>
								<AccountFilter onApply={handleFilterApply} />
							</FormProvider>
						)}
					</div>
					{!accounts?.items.length && !isLoading && hasFilters ? (
						<NoFilteredResults onClear={handleClear} />
					) : (
						<div className="overflow-auto">
							<Table
								className="mt-space-40 max-mobile:mt-space-24"
								columns={isMobile ? mobileColumns : columns}
								disabledRow={checkAccountStatus}
								emptyCellPlaceholder={EMPTY_CELL_PLACEHOLDER}
								isLoading={isLoading}
								onRowClick={handleRowClick}
								renders={renders}
								rows={accounts?.items ?? []}
								showHeader={!isMobile}
							/>
						</div>
					)}
					{accounts?.pagination && accounts.pagination.count > pageSize && (
						<Pagination
							className="mt-space-24!"
							currentPage={currentPage}
							dropdownOpenPosition={DropdownPositions.TOP}
							onPageChange={setCurrentPage}
							onPageSizeChange={handlePageSizeChange}
							pageOptions={pageOptions}
							pageSize={pageSize}
							pageText={pageText}
							totalCount={accounts.pagination.count}
							totalCountText={totalCountText}
						/>
					)}
				</>
			)}
		</PageContainer>
	);
};
